/**
 * Application Routes
 * Defines all routes with lazy loading and role-based access control
 */

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import RoleBasedRoute from './RoleBasedRoute';
import { ROUTES } from './routeConfig';

// Lazy load pages for better performance
// Public pages
const HomePage = lazy(() => import('../pages/public/HomePage'));

// Auth pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));

// Student pages
const StudentDashboardPage = lazy(() => import('../pages/student/StudentDashboardPage'));
const StudentExamsPage = lazy(() => import('../pages/student/ExamsPage'));
const StudentExamDetailPage = lazy(() => import('../pages/student/ExamDetailPage'));
const TakeExamPage = lazy(() => import('../pages/student/TakeExamPage'));
const StudentResultsPage = lazy(() => import('../pages/student/ResultsPage'));
const StudentResultDetailPage = lazy(() => import('../pages/student/ResultDetailPage'));
const StudentProgressPage = lazy(() => import('../pages/student/ProgressPage'));
const StudentProfilePage = lazy(() => import('../pages/student/ProfilePage'));
const StudentSettingsPage = lazy(() => import('../pages/student/SettingsPage'));
const AppsOkayTutorPage = lazy(() => import('../pages/student/AppsOkayTutorPage'));

// Teacher pages
const TeacherDashboardPage = lazy(() => import('../pages/teacher/TeacherDashboardPage'));
const TeacherExamsPage = lazy(() => import('../pages/teacher/ExamsPage'));
const TeacherExamCreatePage = lazy(() => import('../pages/teacher/ExamCreatePage'));
const TeacherExamEditPage = lazy(() => import('../pages/teacher/ExamEditPage'));
const TeacherQuestionsPage = lazy(() => import('../pages/teacher/QuestionsPage'));
const TeacherClassesPage = lazy(() => import('../pages/teacher/ClassesPage'));
const TeacherClassDetailPage = lazy(() => import('../pages/teacher/ClassDetailPage'));
const TeacherGradingPage = lazy(() => import('../pages/teacher/GradingPage'));
const TeacherAnalyticsPage = lazy(() => import('../pages/teacher/AnalyticsPage'));
const TeacherExamAnalyticsPage = lazy(() => import('../pages/teacher/ExamAnalyticsPage'));
const TeacherProfilePage = lazy(() => import('../pages/teacher/ProfilePage'));
const TeacherSettingsPage = lazy(() => import('../pages/teacher/TeacherSettingsPage'));

// Admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('../pages/admin/UsersPage'));
const AdminExamsPage = lazy(() => import('../pages/admin/ExamsPage'));
const AdminOrganizationsPage = lazy(() => import('../pages/admin/OrganizationsPage'));
const AdminSchoolsPage = lazy(() => import('../pages/admin/SchoolsPage'));
const AdminSchoolDetailPage = lazy(() => import('../pages/admin/SchoolDetailPage'));
const AdminSaaSDataPage = lazy(() => import('../pages/admin/SaaSDataPage'));
const AdminSecurityPage = lazy(() => import('../pages/admin/SecurityPage'));
const AdminSettingsPage = lazy(() => import('../pages/admin/AdminSettingsPage'));
const AdminStudentProfileView = lazy(() => import('../pages/admin/StudentProfileView'));
const AdminTeacherProfileView = lazy(() => import('../pages/admin/TeacherProfileView'));
const CORSSupabaseTestingPage = lazy(() => import('../pages/admin/CORSSupabaseTestingPage'));
const ExamCORSupdateTestingPage = lazy(() => import('../pages/admin/ExamCORSupdateTestingPage'));

// Staff pages
const StaffDashboardPage = lazy(() => import('../pages/staff/StaffDashboardPage'));
const StaffTeachersPage = lazy(() => import('../pages/staff/StaffTeachersPage'));
const StaffStudentsPage = lazy(() => import('../pages/staff/StaffStudentsPage'));
const StaffClassesPage = lazy(() => import('../pages/staff/StaffClassesPage'));
const StaffSettingsPage = lazy(() => import('../pages/staff/StaffSettingsPage'));

// Parent pages
const ParentDashboardPage = lazy(() => import('../pages/parent/ParentDashboardPage'));
const ParentSettingsPage = lazy(() => import('../pages/parent/ParentSettingsPage'));

// Student settings (new)
const StudentSettingsPageNew = lazy(() => import('../pages/student/StudentSettingsPage'));

// Editor pages
const EditorDashboardPage = lazy(() => import('../pages/editor/EditorDashboardPage'));
const QuestionBankEditorPage = lazy(() => import('../pages/editor/QuestionBankEditorPage'));
const AppsPage = lazy(() => import('../pages/editor/AppsPage'));
const NotesPage = lazy(() => import('../pages/editor/NotesPage'));
const DesignerPage = lazy(() => import('../pages/editor/DesignerPage'));
const SketchEditorPage = lazy(() => import('../pages/editor/SketchEditorPage'));
const CalculatorEditorPage = lazy(() => import('../pages/editor/CalculatorEditorPage'));
const MathsAIToolsSketchUpPage = lazy(() => import('../pages/editor/MathsAIToolsSketchUpPageWrapper'));
const SketchUpCanvasApiListingsPage = lazy(() => import('../pages/editor/sketchup/SketchUpCanvasApiListingsPage'));
const SketchUpAnimatePage = lazy(() => import('../pages/editor/sketchup/SketchUpAnimatePage'));
const SketchUpAnimatePreviewPage = lazy(() => import('../pages/editor/sketchup/SketchUpAnimatePreviewPage'));
const SketchUpAnimateParamsDemoPage = lazy(() => import('../pages/editor/sketchup/SketchUpAnimateParamsDemoPage'));
const SketchUpAnimateToolboxListingsPage = lazy(() => import('../pages/editor/sketchup/SketchUpAnimateToolboxListingsPage'));
const Hkdse2019Q17SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2019/Hkdse2019Q17SolutionPage'));
const Hkdse2019Q18SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2019/Hkdse2019Q18SolutionPage'));
const Hkdse2019Q19SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2019/Hkdse2019Q19SolutionPage'));
const Hkdse2019Q13SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2019/Hkdse2019Q13SolutionPage'));
const Hkdse2019Q14SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2019/Hkdse2019Q14SolutionPage'));
const Hkdse2018Q13SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2018/Hkdse2018Q13SolutionPage'));
const Hkdse2018Q14SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2018/Hkdse2018Q14SolutionPage'));
const Hkdse2018Q17SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2018/Hkdse2018Q17SolutionPage'));
const Hkdse2018Q18SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2018/Hkdse2018Q18SolutionPage'));
const Hkdse2018Q19SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2018/Hkdse2018Q19SolutionPage'));
const Hkdse2017Q10SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2017/Hkdse2017Q10SolutionPage'));
const Hkdse2017Q11SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2017/Hkdse2017Q11SolutionPage'));
const Hkdse2017Q12SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2017/Hkdse2017Q12SolutionPage'));
const Hkdse2017Q13SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2017/Hkdse2017Q13SolutionPage'));
const Hkdse2017Q18SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2017/Hkdse2017Q18SolutionPage'));
const Hkdse2017Q19SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2017/Hkdse2017Q19SolutionPage'));
const Hkdse2015Q08SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q08SolutionPage'));
const Hkdse2015Q09SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q09SolutionPage'));
const Hkdse2015Q10SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q10SolutionPage'));
const Hkdse2015Q11SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q11SolutionPage'));
const Hkdse2015Q12SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q12SolutionPage'));
const Hkdse2015Q13SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q13SolutionPage'));
const Hkdse2015Q14SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q14SolutionPage'));
const Hkdse2015Q15SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q15SolutionPage'));
const Hkdse2015Q16SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q16SolutionPage'));
const Hkdse2015Q17SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q17SolutionPage'));
const Hkdse2015Q18SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q18SolutionPage'));
const Hkdse2015Q19SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2015/Hkdse2015Q19SolutionPage'));
const Hkdse2016Q07SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q07SolutionPage'));
const Hkdse2016Q10SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q10SolutionPage'));
const Hkdse2016Q11SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q11SolutionPage'));
const Hkdse2016Q12SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q12SolutionPage'));
const Hkdse2016Q13SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q13SolutionPage'));
const Hkdse2016Q15SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q15SolutionPage'));
const Hkdse2016Q17SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q17SolutionPage'));
const Hkdse2016Q18SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q18SolutionPage'));
const Hkdse2016Q19SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q19SolutionPage'));
const Hkdse2016Q20SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2016/Hkdse2016Q20SolutionPage'));
const Hkdse2012Q08SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2012/Hkdse2012Q08SolutionPage'));
const Hkdse2013Q07SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2013/Hkdse2013Q07SolutionPage'));
const Hkdse2013Q14SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2013/Hkdse2013Q14SolutionPage'));
const Hkdse2013Q18SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2013/Hkdse2013Q18SolutionPage'));
const Hkdse2014Q12SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2014/Hkdse2014Q12SolutionPage'));
const Hkdse2014Q13SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2014/Hkdse2014Q13SolutionPage'));
const Hkdse2014Q14SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2014/Hkdse2014Q14SolutionPage'));
const Hkdse2014Q17SolutionPage = lazy(() => import('../pages/editor/sketchup/manim-api/animate/2014/Hkdse2014Q17SolutionPage'));
const SketchUpToolBoxPage = lazy(() => import('../pages/toolbox/SketchUpToolBoxPage'));
const SketchUpEnhancedToolBoxPage = lazy(() => import('../pages/toolbox/SketchUpEnhancedToolBoxPage'));
const MyStudyPage = lazy(() => import('../pages/editor/MyStudyPage'));
const HkdseMathsPaper1ListingsPage = lazy(() => import('../pages/toolbox/HkdseMathsPaper1ListingsPage'));
const HkdseMathsPaper2ListingsPage = lazy(() => import('../pages/toolbox/HkdseMathsPaper2ListingsPage'));
const ESSToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/ESSToolsBoxApiLibraryFullListPage'));
const IdiomsToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/IdiomsToolsBoxApiLibraryFullListPage'));
const VerbsToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/VerbsToolsBoxApiLibraryFullListPage'));
const PhrasalVerbToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/PhrasalVerbToolsBoxApiLibraryFullListPage'));
const LCToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/LCToolsBoxApiLibraryFullListPage'));
const AccountToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/AccountToolsBoxApiLibraryFullListPage'));
const BusinessToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/BusinessToolsBoxApiLibraryFullListPage'));
const BiologyToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/BiologyToolsBoxApiLibraryFullListPage'));
const CHistoryToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/CHistoryToolsBoxApiLibraryFullListPage'));
const ICTToolsBoxAllApiLibraryFullListPage = lazy(() => import('../pages/toolbox/ICTToolsBoxAllApiLibraryFullListPage'));
const HistoryToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/HistoryToolsBoxApiLibraryFullListPage'));
const VocabToolsBoxApiLibraryFullListPage = lazy(() => import('../pages/toolbox/VocabToolsBoxApiLibraryFullListPage'));
const ToolboxAgentPage = lazy(() => import('../pages/toolbox/ToolboxAgentPage'));
const PlaygroundPage = lazy(() => import('../pages/playground/PlaygroundPage'));
// Learns2gether School Education Hub + 6 Agent Flow pages (Campus group)
const Learns2getherSchoolEducationHub = lazy(() => import('../pages/agent_flow/Learns2getherSchoolEducationHub'));
const PrincipalKpiDashboardPage = lazy(() => import('../pages/agent_flow/PrincipalKpiDashboardPage'));
const TeacherWorkflowPage = lazy(() => import('../pages/agent_flow/TeacherWorkflowPage'));
const TeacherDailySchoolLifePage = lazy(() => import('../pages/agent_flow/TeacherDailySchoolLifePage'));
const StudentLearningProfilePage = lazy(() => import('../pages/agent_flow/StudentLearningProfilePage'));
const StudentExamLifecyclePage = lazy(() => import('../pages/agent_flow/StudentExamLifecyclePage'));
const StudentDailySchoolLifePage = lazy(() => import('../pages/agent_flow/StudentDailySchoolLifePage'));

// Error pages
const NotFoundPage = lazy(() => import('../pages/errors/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('../pages/errors/UnauthorizedPage'));
const ForbiddenPage = lazy(() => import('../pages/errors/ForbiddenPage'));
const ServerErrorPage = lazy(() => import('../pages/errors/ServerErrorPage'));

// Loading fallback: self-contained to avoid invalid element if common/Loading is ever undefined
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-exam-surface text-gray-500">
    <div className="w-16 h-16 border-4 border-exam-primary border-t-transparent rounded-full animate-spin" />
    <span className="mt-4">Loading...</span>
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path={ROUTES.HOME} element={<HomePage />} />
        
        {/* Auth Routes */}
        <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.AUTH.REGISTER} element={<RegisterPage />} />
        <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        
        {/* Student Routes */}
        <Route
          path={ROUTES.STUDENT.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAMS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentExamsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.EXAM_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentExamDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.TAKE_EXAM}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <TakeExamPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.RESULTS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentResultsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.RESULT_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentResultDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROGRESS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentProgressPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentProfilePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin']}>
              <StudentSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT.APPSOKAY_TUTOR}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'staff', 'admin']}>
              <AppsOkayTutorPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Teacher Routes */}
        <Route
          path={ROUTES.TEACHER.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAMS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_CREATE}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamCreatePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_EDIT}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamEditPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.QUESTIONS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherQuestionsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASSES}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherClassesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.CLASS_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherClassDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.GRADING}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherGradingPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.ANALYTICS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherAnalyticsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.EXAM_ANALYTICS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherExamAnalyticsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherProfilePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.TEACHER.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['teacher', 'admin']}>
              <TeacherSettingsPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path={ROUTES.ADMIN.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.USERS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminUsersPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.EXAMS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminExamsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.ORGANIZATIONS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminOrganizationsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SECURITY}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSecurityPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SCHOOLS}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSchoolsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SCHOOL_DETAIL}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSchoolDetailPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.SAAS_DATA}
          element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminSaaSDataPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.CORS_SUPABASE_TESTING}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher']}>
              <CORSSupabaseTestingPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.EXAM_CORS_TESTING}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher']}>
              <ExamCORSupdateTestingPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.STUDENT_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'staff']}>
              <AdminStudentProfileView />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN.TEACHER_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'staff']}>
              <AdminTeacherProfileView />
            </RoleBasedRoute>
          }
        />
        
        {/* Staff Routes */}
        <Route
          path={ROUTES.STAFF.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.TEACHERS}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffTeachersPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.STUDENTS}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffStudentsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.CLASSES}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffClassesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <StaffSettingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.STUDENT_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <AdminStudentProfileView />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.STAFF.TEACHER_VIEW}
          element={
            <RoleBasedRoute allowedRoles={['staff', 'admin']}>
              <AdminTeacherProfileView />
            </RoleBasedRoute>
          }
        />
        
        {/* Editor Routes - Admin and Teacher */}
        <Route
          path={ROUTES.EDITOR.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher']}>
              <QuestionBankEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.DASHBOARD_HUB}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <EditorDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TABLE}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher']}>
              <QuestionBankEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_ENGLISH}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_CHINESE}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_ESSTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_ECAMPUS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_MYCLASS}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_MYSTUDY}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.LEARNS2GETHER_HUB}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <Learns2getherSchoolEducationHub />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.AGENT_FLOW_PRINCIPAL_KPI}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PrincipalKpiDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.AGENT_FLOW_TEACHER_WORKFLOW}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <TeacherWorkflowPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.AGENT_FLOW_TEACHER_DAILY}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <TeacherDailySchoolLifePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.AGENT_FLOW_STUDENT_PROFILE}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <StudentLearningProfilePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.AGENT_FLOW_STUDENT_EXAM}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <StudentExamLifecyclePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.AGENT_FLOW_STUDENT_DAILY}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <StudentDailySchoolLifePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_LCTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_CHISTORYTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_HISTORYTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_VOCABTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_AIGCTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_BAFSTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_ACCOUNTTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_BUSINESSTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_BIOLOGYTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_ICTTOOLSBOX}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PLAYGROUND_MATHS_ENHANCED}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <PlaygroundPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.APPS}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <AppsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.NOTES}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <NotesPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.DESIGNER}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <DesignerPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCH_EDITOR}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.CALCULATOR_EDITOR}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <CalculatorEditorPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_CANVAS_API_LISTINGS}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpCanvasApiListingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpAnimatePage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_PREVIEW}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpAnimatePreviewPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_PARAMS_DEMO}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpAnimateParamsDemoPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_TOOLBOX_LISTINGS}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpAnimateToolboxListingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2019_Q17}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <Hkdse2019Q17SolutionPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2019_Q18}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <Hkdse2019Q18SolutionPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2019_Q19}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <Hkdse2019Q19SolutionPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2019_Q13}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <Hkdse2019Q13SolutionPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2019_Q14}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <Hkdse2019Q14SolutionPage />
            </RoleBasedRoute>
          }
        />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2018_Q13} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2018Q13SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2018_Q14} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2018Q14SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2018_Q17} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2018Q17SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2018_Q18} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2018Q18SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2018_Q19} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2018Q19SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2017_Q10} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2017Q10SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2017_Q11} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2017Q11SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2017_Q12} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2017Q12SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2017_Q13} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2017Q13SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2017_Q18} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2017Q18SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2017_Q19} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2017Q19SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q08} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q08SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q09} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q09SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q10} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q10SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q11} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q11SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q12} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q12SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q13} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q13SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q14} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q14SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q15} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q15SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q16} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q16SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q17} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q17SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q18} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q18SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2015_Q19} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2015Q19SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q07} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q07SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q10} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q10SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q11} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q11SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q12} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q12SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q13} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q13SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q15} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q15SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q17} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q17SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q18} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q18SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q19} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q19SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2016_Q20} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2016Q20SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2012_Q08} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2012Q08SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2013_Q07} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2013Q07SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2013_Q14} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2013Q14SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2013_Q18} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2013Q18SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2014_Q12} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2014Q12SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2014_Q13} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2014Q13SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2014_Q14} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2014Q14SolutionPage /></RoleBasedRoute>} />
        <Route path={ROUTES.EDITOR.SKETCHUP_ANIMATE_HKDSE_2014_Q17} element={<RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}><Hkdse2014Q17SolutionPage /></RoleBasedRoute>} />
        <Route
          path={ROUTES.EDITOR.MATHSAITOOLS_SKETCHUP}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <MathsAIToolsSketchUpPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpToolBoxPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX_ENHANCED}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <SketchUpEnhancedToolBoxPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.MY_STUDY}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <MyStudyPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX_HKDSE_LISTINGS}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <HkdseMathsPaper1ListingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX_HKDSE_LISTINGS_P2}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <HkdseMathsPaper2ListingsPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.ESSTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <ESSToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.IDIOMSTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <IdiomsToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.VERBSTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <VerbsToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.PHRASALVERBTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <PhrasalVerbToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.LCTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <LCToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.ACCOUNTTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <AccountToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.BUSINESSTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <BusinessToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.BIOLOGYTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <BiologyToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.CHISTORYTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <CHistoryToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.ICTTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <ICTToolsBoxAllApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.HISTORYTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <HistoryToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.VOCABTOOLSBOX_API_FULL_LIST}
          element={
            <RoleBasedRoute allowedRoles={['student', 'admin', 'teacher', 'staff']}>
              <VocabToolsBoxApiLibraryFullListPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX_AGENT_TOOL_SUBTOOL}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <ToolboxAgentPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX_AGENT_TOOL}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <ToolboxAgentPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.EDITOR.TOOLBOX_AGENT}
          element={
            <RoleBasedRoute allowedRoles={['admin', 'teacher', 'staff']}>
              <ToolboxAgentPage />
            </RoleBasedRoute>
          }
        />

        {/* Parent Routes */}
        <Route
          path={ROUTES.PARENT.DASHBOARD}
          element={
            <RoleBasedRoute allowedRoles={['parent', 'admin']}>
              <ParentDashboardPage />
            </RoleBasedRoute>
          }
        />
        <Route
          path={ROUTES.PARENT.SETTINGS}
          element={
            <RoleBasedRoute allowedRoles={['parent', 'admin']}>
              <ParentSettingsPage />
            </RoleBasedRoute>
          }
        />
        
        {/* Error Routes */}
        <Route path={ROUTES.ERRORS.UNAUTHORIZED} element={<UnauthorizedPage />} />
        <Route path={ROUTES.ERRORS.FORBIDDEN} element={<ForbiddenPage />} />
        <Route path={ROUTES.ERRORS.SERVER_ERROR} element={<ServerErrorPage />} />
        <Route path={ROUTES.ERRORS.NOT_FOUND} element={<NotFoundPage />} />
        
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to={ROUTES.ERRORS.NOT_FOUND} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
